import { MouseEvent, useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { User, ShoppingCart, Menu } from 'lucide-react';
import { Button } from './ui/button';
import type { siteContent } from '../data/siteContent';
import logoBlack from '../../logo-black.webp';
import { useIsMobile } from './ui/use-mobile';
import { MobileNavDrawer } from './MobileNavDrawer';
import { LanguageMenu } from './LanguageMenu';
import { useTranslation } from '../context/LanguageContext';

interface HeaderProps {
  brand: typeof siteContent.brand;
  navigation: string[];
  cartCount: number;
  onCartToggle: () => void;
}

export function Header({
  brand,
  navigation,
  cartCount,
  onCartToggle,
}: HeaderProps) {
  const navItems = navigation;
  const location = useLocation();
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const isMobile = useIsMobile();
  const logoHeight = isMobile ? '4.5rem' : '6.5rem';
  const logoAspectRatio = 512 / 288;
  const { t } = useTranslation();

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (isMobileNavOpen && isMobile) {
      const previous = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = previous;
      };
    }
    return undefined;
  }, [isMobileNavOpen, isMobile]);

  useEffect(() => {
    if (!isMobile && isMobileNavOpen) {
      setIsMobileNavOpen(false);
    }
  }, [isMobile, isMobileNavOpen]);

  useEffect(() => {
    // Close the mobile menu when navigating to a new route
    setIsMobileNavOpen(false);
  }, [location.pathname]);

  const handleLogoClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (location.pathname === '/') {
      event.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-header-surface border-b border-border shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            aria-label="Go to homepage"
            className="group flex items-center gap-3 shrink-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary touch-manipulation"
            onClick={handleLogoClick}
          >
            <img
              src={logoBlack}
              alt={`${brand.name} logo`}
              className="block h-full w-auto object-contain pointer-events-none select-none"
              style={{ height: logoHeight, aspectRatio: `${logoAspectRatio}` }}
              loading="lazy"
            />
          </Link>

          {/* Center Navigation - Hidden on mobile */}
          <nav className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => {
              const path =
                item === 'Home'
                  ? '/'
                  : item === 'Artworks'
                    ? '/artworks'
                    : `/${item.toLowerCase()}`
              const navLabel = t(`nav.${item.toLowerCase()}`, item)
              return (
                <NavLink
                  key={item}
                  to={path}
                  className={({ isActive }) =>
                    `transition-colors ${
                      isActive || location.pathname === path
                        ? 'text-primary'
                        : 'text-foreground/70 hover:text-primary'
                    }`
                  }
                >
                  {navLabel}
                </NavLink>
              )
            })}
          </nav>

          {/* Right Icons */}
          <div className="flex items-center gap-2 sm:gap-4">
            <LanguageMenu />
            {/* <Button
              variant="ghost"
              size="icon"
              className="hidden sm:flex"
              aria-label="Account"
            >
              <User className="h-5 w-5" />
            </Button> */}
            <Button
              variant="ghost"
              size="icon"
              className="relative"
              onClick={onCartToggle}
            >
              <span className="sr-only">
                {cartCount > 0 ? `View cart, ${cartCount} item${cartCount === 1 ? '' : 's'}` : 'View cart'}
              </span>
              <ShoppingCart className="h-5 w-5" />
              {cartCount > 0 && (
                <span
                  className="absolute -top-1 -right-1 bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-semibold leading-none shadow-sm"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  {cartCount}
                </span>
              )}
            </Button>
            {isMobile && (
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                aria-label="Toggle menu"
                aria-expanded={isMobileNavOpen}
                aria-controls="mobile-navigation"
                onClick={() => setIsMobileNavOpen((prev) => !prev)}
              >
              <Menu className="h-7 w-7" />
              </Button>
            )}
          </div>
        </div>
      </div>
      {isMobile && isMobileNavOpen && (
        <MobileNavDrawer brand={brand} navItems={navItems} onClose={() => setIsMobileNavOpen(false)} />
      )}
    </header>
  );
}
